import i18n from '@/utils/i18n';
import React, { useEffect, useState } from 'react';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { CreditPolicy } from '@/components/credit-request/credit-policy';
import { CreditRequestBasketItem } from '@/components/credit-request/credit-request-basket-item';
import { formatCurrency, getCountryCurrencyCode } from '@/utils/money';
import { KeyboardAwareRecyclerList } from '@/components/recycler-list';
import { SCREEN_PADDING, SCREEN_PADDING_SMALL } from '@/styles';
import {
  selectTotalPriceInMicros,
  selectTotalQuantity,
  useCreditRequestInvoiceStore,
} from '@/stores/credit-request-invoice-store';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { View } from 'react-native';

export interface CreditRequestInvoiceDetailsContentProps {
  onEmpty: () => void;
  onSubmit: () => Promise<void> | void;
  orderInvoiceUUID: string;
  submitLabel?: string;
}

export const CreditRequestInvoiceDetailsContent: React.FC<
  CreditRequestInvoiceDetailsContentProps
> = ({ onEmpty, onSubmit, orderInvoiceUUID, submitLabel }) => {
  const [showIneligibleItemInfo, setShowIneligibleItemInfo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const batchItems = useCreditRequestInvoiceStore((s) => s.batchItems);
  const removeBatchItem = useCreditRequestInvoiceStore(
    (s) => s.removeBatchItem,
  );
  const totalPriceInMicros = useCreditRequestInvoiceStore((s) =>
    selectTotalPriceInMicros(s),
  );
  const totalQuantity = useCreditRequestInvoiceStore((s) =>
    selectTotalQuantity(s),
  );
  const updateBatchItemQuantity = useCreditRequestInvoiceStore(
    (s) => s.updateBatchItemQuantity,
  );
  const {
    selectedRestaurantRelationship: { countryCode },
  } = useAuthenticatedUserContext();
  const currencyCode = countryCode
    ? getCountryCurrencyCode(countryCode)
    : undefined;
  const [styles, theme] = useStyles();
  const { bottom } = useSafeAreaInsets();
  const { credit_policy: creditPolicy } =
    useDynamicConfigValue('credit_request_config') ?? {};

  useEffect(() => {
    if (batchItems.length === 0) {
      onEmpty();
    }
  }, [batchItems.length, onEmpty]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <View style={styles.flex}>
        {batchItems.length > 0 ? (
          <KeyboardAwareRecyclerList
            alwaysBounceVertical={false}
            contentContainerStyle={styles.batchListContainer}
            estimatedItemSize={180}
            keyboardDismissMode="on-drag"
            keyboardShouldPersistTaps="handled"
            renderItem={({ item }) => (
              <CreditRequestBasketItem
                batchLineItem={item}
                key={item.uuid}
                maxQuantity={item.maxQuantity}
                onInfoIconPress={() => setShowIneligibleItemInfo(true)}
                removeFromBasket={removeBatchItem}
                updateItemQuantity={updateBatchItemQuantity}
              />
            )}
            renderSectionHeader={() => null}
            sections={[
              {
                data: batchItems,
                key: 'credit-request-invoice-details',
              },
            ]}
            shouldRenderSeparator={() => false}
            stickyHeaderIndices={[]}
            testID="credit-request-invoice-details-list"
          />
        ) : null}
      </View>
      <View
        style={[
          styles.bottomActionWrapper,
          { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
        ]}
      >
        <View style={styles.pageFooterRow}>
          <Typography
            color={theme.sk.color.text.primary}
            variant="paragraph-base"
          >
            {i18n.t(totalQuantity === 1 ? 'unit_count' : 'unit_count_plural', {
              count: totalQuantity,
            })}
          </Typography>
          <Typography
            color={theme.sk.color.text.primary}
            variant="paragraph-base-600"
          >
            {formatCurrency(totalPriceInMicros, currencyCode)}
          </Typography>
        </View>
        <Button
          disabled={isSubmitting}
          loading={isSubmitting}
          onPress={handleSubmit}
          trackingData={{
            name: 'credit_request_invoice_details_screen.submit',
            properties: {
              orderInvoiceUUID,
            },
          }}
        >
          {submitLabel ?? i18n.t('credit_request_scan.screen.submit')}
        </Button>
      </View>
      {showIneligibleItemInfo && creditPolicy ? (
        <BaseActionSheet
          isVisible
          onClose={() => setShowIneligibleItemInfo(false)}
        >
          <View style={styles.creditPolicyWrapper}>
            <View style={styles.creditPolicyHeader}>
              <Typography variant="heading-lg">
                {i18n.t('ineligible_item.title')}
              </Typography>
            </View>
            <CreditPolicy creditPolicy={creditPolicy} />
          </View>
        </BaseActionSheet>
      ) : null}
    </>
  );
};

const useStyles = createStyles(() => ({
  batchListContainer: {
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
  bottomActionWrapper: {
    paddingHorizontal: SCREEN_PADDING,
    paddingTop: SCREEN_PADDING_SMALL,
  },
  creditPolicyHeader: {
    marginBottom: 16,
  },
  creditPolicyWrapper: {
    marginBottom: 16,
  },
  flex: {
    flex: 1,
  },
  pageFooterRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 16,
  },
}));
